import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { AIChatbot } from './AIChatbot';
import './ChatbotToggle.scss';

interface ChatbotToggleProps {
    className?: string;
}

export const ChatbotToggle: React.FC<ChatbotToggleProps> = ({ className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasNewMessage, setHasNewMessage] = useState(false);

    const { user } = useSelector((state: RootState) => state.auth);

    // Only show for SuperAdmin and Admin users
    const canAccessChatbot = user?.role?.includes('SuperAdmin') || user?.role?.includes('P3MSuperAdmin') || user?.role?.includes('Admin');

    if (!canAccessChatbot) {
        return null;
    }

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setHasNewMessage(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    // Rendered into <body>, not in place.
    //
    // The toggle is `position: fixed` and lives inside the app shell's
    // animated content wrapper. Any transform, filter or contain on an
    // ancestor turns that wrapper into the containing block, so the button
    // stops being pinned to the viewport and lands in the content column
    // instead - which is what it was doing, sitting between the navbar and
    // the page and pushing the page down. A portal takes it out of that
    // subtree entirely, so no ancestor can move it and it occupies no space
    // in the page flow.
    return createPortal(
        <>
            <div className={`chatbot-toggle ${className}`}>
                <button
                    className={`toggle-button ${isOpen ? 'open' : ''} ${hasNewMessage ? 'has-message' : ''}`}
                    onClick={handleToggle}
                    aria-label={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
                    title={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
                >
                    <div className="button-content">
                        {isOpen ? (
                            <span className="close-icon">✕</span>
                        ) : (
                            <>
                                <span className="bot-icon">🤖</span>
                                <span className="ai-text">AI</span>
                                {hasNewMessage && <div className="notification-dot" />}
                            </>
                        )}
                    </div>
                </button>

                {!isOpen && (
                    <div className="tooltip">
                        <span>AI Assistant</span>
                        <div className="tooltip-arrow" />
                    </div>
                )}
            </div>

            <AIChatbot
                isOpen={isOpen}
                onClose={handleClose}
                className={className}
            />
        </>,
        document.body
    );
}; 